import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Badge, Modal, Tabs } from '../../../components/ui';
import { Package, Plus, Edit, Trash2, ToggleLeft, ToggleRight, Store, RefreshCw, ChevronDown, ChevronRight, Check, AlertTriangle, Save, X, Ban, RotateCcw, Wrench } from 'lucide-react';
import { fetchAPI } from '../../../services/apiClient';

// ── Tipos ────────────────────────────────────────────────

interface PremioItem {
  tipo_id: string;
  concepto: string;
  multiplicador: string;
  es_extranjera?: boolean;
}

interface ConfigItem {
  tipo_id: string;
  commission_percentage: string | null;
  commission_exempt_amount: string | null;
  commission_max_amount: string | null;
  max_number_sales: number | null;
  limite_jugada: string | null;
  alcance_limite_jugada: string | null;
  es_extranjera?: boolean;
}

interface PaqueteResumen {
  id: string;
  nombre: string;
  descripcion: string | null;
  activo: boolean;
  locales_asignados: number;
  created_at: string | null;
  updated_at: string | null;
}

interface RestringidoItem {
  tipo_id: string;
  numeros: string[];
  auto_dia?: boolean;
  max_number_sales?: number | null;
  riesgo_pct?: string | null;
  minimo_permitido?: string | null;
  es_extranjera?: boolean;
}

interface PaqueteDetalle extends PaqueteResumen {
  premios: PremioItem[];
  configs: ConfigItem[];
  restringidos: RestringidoItem[];
  locales: { id: string; nombre: string }[];
}

interface LocalConPaquete {
  id: string;
  nombre: string;
  es_propio: boolean;
  paquete_reglas_id: string | null;
  paquete_nombre: string | null;
}

const CONCEPTOS_POR_TIPO: Record<string, string[]> = {
  NORMAL: ['DIRECTO'],
  REVENTADO: ['DIRECTO', 'REVENTADO'],
  MEGA: ['DIRECTO', 'REVENTADO', 'MEGA_REVENTADO'],
  MONAZOS: ['EN_ORDEN', 'DESORDEN', 'PRIMEROS_2', 'ULTIMOS_2'],
  PARLEY: ['DIRECTO'],
  FECHAS: ['DIRECTO'],
};

const TIPOS_ORDEN = ['NORMAL', 'REVENTADO', 'MONAZOS', 'PARLEY', 'FECHAS'];

const TIPOS_CON_RESTRINGIDOS = new Set(['NORMAL', 'REVENTADO']);

const CONCEPTO_LABELS: Record<string, string> = {
  DIRECTO: 'Directo',
  REVENTADO: 'Reventado',
  MEGA_REVENTADO: 'Mega Reventado',
  EN_ORDEN: 'En Orden',
  DESORDEN: 'Desorden',
  PRIMEROS_2: 'Primeros 2',
  ULTIMOS_2: 'Últimos 2',
};

// ── Tab 1: Listado de Paquetes ──────────────────────────

const PaquetesListTab: React.FC = () => {
  const [paquetes, setPaquetes] = useState<PaqueteResumen[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando] = useState<PaqueteDetalle | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<PaqueteResumen | null>(null);


  const cargar = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchAPI<PaqueteResumen[]>('/paquetes-reglas');
      setPaquetes(data);
    } catch (e: any) {
      setError(e.message || 'Error al cargar paquetes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirCrear = () => {
    setEditando(null);
    setModalOpen(true);
  };

  const abrirEditar = async (id: string) => {
    try {
      const detalle = await fetchAPI<PaqueteDetalle>(`/paquetes-reglas/${id}`);
      setEditando(detalle);
      setModalOpen(true);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const toggleEstado = async (id: string) => {
    try {
      await fetchAPI<any>(`/paquetes-reglas/${id}/estado`, { method: 'PATCH' });
      cargar();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const eliminar = async (id: string) => {
    try {
      await fetchAPI<any>(`/paquetes-reglas/${id}`, { method: 'DELETE' });
      setConfirmDelete(null);
      cargar();
    } catch (e: any) {
      setError(e.message);
      setConfirmDelete(null);
    }
  };

  const reaplicar = async (id: string, nombre: string) => {
    if (!confirm(`¿Reaplicar paquete "${nombre}" a todos sus locales asignados?`)) return;
    setError('');
    setMensaje('');
    try {
      const res = await fetchAPI<any>(`/paquetes-reglas/${id}/reaplicar`, { method: 'POST' });
      setMensaje(res.mensaje || `Reaplicado a ${res.locales_actualizados} local(es)`);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const repararReglas = async (id: string, nombre: string) => {
    if (!confirm(`¿Reparar reglas faltantes del paquete "${nombre}"?\n\nEsto detecta sorteos existentes y regenera reglas operativas que no se hayan estampado (ej: MONAZOS).`)) return;
    setError('');
    setMensaje('');
    try {
      const res = await fetchAPI<any>(`/paquetes-reglas/${id}/reparar-reglas`, { method: 'POST' });
      setMensaje(res.mensaje || 'Reparación completada');
    } catch (e: any) {
      setError(e.message);
    }
  };


  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Paquetes de Reglas</h1>
          <p className="text-gray-500">Plantillas reutilizables de reglas comerciales por tipo de juego</p>
        </div>
        <div className="flex gap-2">
          <button onClick={cargar} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg" title="Refrescar">
            <RefreshCw size={20} />
          </button>
          <Button variant="primary" onClick={abrirCrear} leftIcon={<Plus size={18} />}>
            Nuevo Paquete
          </Button>
        </div>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>}
      {mensaje && <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700">{mensaje}</div>}

      <Card padding="none">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Cargando...</div>
        ) : paquetes.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No hay paquetes creados. Cree uno para comenzar.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Nombre</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Descripción</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-gray-700">Locales</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-gray-700">Estado</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-gray-700">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {paquetes.map(p => (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Package size={16} className="text-blue-500" />
                        <span className="font-medium text-gray-900">{p.nombre}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{p.descripcion || '-'}</td>
                    <td className="px-4 py-3 text-center">
                      <Badge variant={p.locales_asignados > 0 ? 'primary' : 'neutral'}>
                        {p.locales_asignados} local{p.locales_asignados !== 1 ? 'es' : ''}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge variant={p.activo ? 'success' : 'neutral'}>
                        {p.activo ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1 justify-center">
                        <button onClick={() => abrirEditar(p.id)} className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200" title="Editar">
                          <Edit size={14} />
                        </button>
                        <button onClick={() => toggleEstado(p.id)} className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200" title={p.activo ? 'Desactivar' : 'Activar'}>
                          {p.activo ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                        </button>
                        <button
                          onClick={() => setConfirmDelete(p)}
                          disabled={p.locales_asignados > 0}
                          className={`px-3 py-1.5 text-xs font-medium rounded-lg border ${p.locales_asignados > 0 ? 'text-gray-400 bg-gray-50 border-gray-200 cursor-not-allowed' : 'text-red-700 bg-red-50 hover:bg-red-100 border-red-200'}`}
                          title={p.locales_asignados > 0 ? 'Desasigne locales primero' : 'Eliminar'}
                        >
                          <Trash2 size={14} />
                        </button>
                        {p.locales_asignados > 0 && (
                          <>
                            <button
                              onClick={() => reaplicar(p.id, p.nombre)}
                              className="px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-lg border border-green-200"
                              title="Reaplicar paquete a todos sus locales"
                            >
                              <RotateCcw size={14} />
                            </button>
                            <button
                              onClick={() => repararReglas(p.id, p.nombre)}
                              className="px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg border border-amber-200"
                              title="Reparar reglas faltantes (MONAZOS)"
                            >
                              <Wrench size={14} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Modal Crear/Editar */}
      {modalOpen && (
        <PaqueteFormModal
          editando={editando}
          onClose={() => { setModalOpen(false); setEditando(null); }}
          onSaved={() => { setModalOpen(false); setEditando(null); cargar(); }}
        />
      )}

      {/* Confirm Delete */}
      <Modal isOpen={!!confirmDelete} onClose={() => setConfirmDelete(null)} title="Eliminar paquete">
        <div className="space-y-4">
          <p className="text-gray-700">
            ¿Eliminar el paquete <strong>{confirmDelete?.nombre}</strong>? Esta acción no se puede deshacer.
          </p>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancelar</Button>
            <Button variant="danger" onClick={() => confirmDelete && eliminar(confirmDelete.id)}>Eliminar</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};


// ── Modal Crear/Editar Paquete ──────────────────────────

interface PaqueteFormModalProps {
  editando: PaqueteDetalle | null;
  onClose: () => void;
  onSaved: () => void;
}

const PaqueteFormModal: React.FC<PaqueteFormModalProps> = ({ editando, onClose, onSaved }) => {
  // ── Helpers para indexar por (tipo, variante) ──
  // Variante: false = nacional, true = extranjera.
  const keyVar = (tipo: string, extr: boolean): string => `${tipo}|${extr ? 1 : 0}`;

  // Convierte el valor de un input numérico a number|null preservando el 0 como valor válido.
  // Solo trata '', null y undefined como "sin valor".
  const numOrNull = (v: unknown): number | null => {
    if (v === '' || v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  // Devuelve true si el valor es distinto de '', null y undefined (acepta 0).
  const hasValue = (v: unknown): boolean => v !== '' && v !== null && v !== undefined;

  const [nombre, setNombre] = useState(editando?.nombre || '');
  const [descripcion, setDescripcion] = useState(editando?.descripcion || '');
  const [tiposActivos, setTiposActivos] = useState<Set<string>>(() => {
    if (!editando) return new Set<string>();
    const tipos = new Set<string>();
    editando.premios.forEach(p => tipos.add(p.tipo_id));
    editando.configs.forEach(c => tipos.add(c.tipo_id));
    return tipos;
  });
  // Marca por tipo si el banquero definió explícitamente la variante extranjera.
  const [extranjeraActiva, setExtranjeraActiva] = useState<Record<string, boolean>>(() => {
    const map: Record<string, boolean> = {};
    if (editando) {
      editando.premios.forEach(p => { if (p.es_extranjera) map[p.tipo_id] = true; });
      editando.configs.forEach(c => { if (c.es_extranjera) map[c.tipo_id] = true; });
      editando.restringidos?.forEach(r => { if (r.es_extranjera) map[r.tipo_id] = true; });
    }
    return map;
  });
  // premios[tipoVar][concepto] = multiplicador
  const [premios, setPremios] = useState<Record<string, Record<string, string>>>(() => {
    const map: Record<string, Record<string, string>> = {};
    if (editando) {
      editando.premios.forEach(p => {
        const k = keyVar(p.tipo_id, !!p.es_extranjera);
        if (!map[k]) map[k] = {};
        map[k][p.concepto] = p.multiplicador;
      });
    }
    return map;
  });
  const [configs, setConfigs] = useState<Record<string, ConfigItem>>(() => {
    const map: Record<string, ConfigItem> = {};
    if (editando) {
      editando.configs.forEach(c => {
        map[keyVar(c.tipo_id, !!c.es_extranjera)] = c;
      });
    }
    return map;
  });
  const [restringidos, setRestringidos] = useState<Record<string, string[]>>(() => {
    const map: Record<string, string[]> = {};
    if (editando?.restringidos) {
      editando.restringidos.forEach(r => {
        map[keyVar(r.tipo_id, !!r.es_extranjera)] = r.numeros;
      });
    }
    return map;
  });
  const [autoDia, setAutoDia] = useState<Record<string, boolean>>(() => {
    const map: Record<string, boolean> = {};
    if (editando?.restringidos) {
      editando.restringidos.forEach(r => {
        map[keyVar(r.tipo_id, !!r.es_extranjera)] = r.auto_dia || false;
      });
    }
    return map;
  });
  const [restLimites, setRestLimites] = useState<Record<string, { max_number_sales?: number | null; riesgo_pct?: string | null; minimo_permitido?: string | null }>>(() => {
    const map: Record<string, { max_number_sales?: number | null; riesgo_pct?: string | null; minimo_permitido?: string | null }> = {};
    if (editando?.restringidos) {
      editando.restringidos.forEach(r => {
        map[keyVar(r.tipo_id, !!r.es_extranjera)] = {
          max_number_sales: r.max_number_sales,
          riesgo_pct: r.riesgo_pct,
          minimo_permitido: r.minimo_permitido,
        };
      });
    }
    return map;
  });
  const [numeroInput, setNumeroInput] = useState<Record<string, string>>({});
  const [expandedTipos, setExpandedTipos] = useState<Set<string>>(
    () => editando ? new Set(tiposActivos) : new Set()
  );
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  const toggleTipo = (tipo: string) => {
    setTiposActivos(prev => {
      const next = new Set(prev);
      if (next.has(tipo)) {
        next.delete(tipo);
      } else {
        next.add(tipo);
        setExpandedTipos(e => new Set(e).add(tipo));
      }
      return next;
    });
  };

  const toggleExpand = (tipo: string) => {
    setExpandedTipos(prev => {
      const next = new Set(prev);
      next.has(tipo) ? next.delete(tipo) : next.add(tipo);
      return next;
    });
  };

  const setMultiplicador = (tipoVar: string, concepto: string, val: string) => {
    setPremios(prev => ({
      ...prev,
      [tipoVar]: { ...(prev[tipoVar] || {}), [concepto]: val },
    }));
  };

  const setConfigField = (tipoVar: string, field: string, val: any) => {
    setConfigs(prev => ({
      ...prev,
      [tipoVar]: { ...(prev[tipoVar] || { tipo_id: tipoVar.split('|')[0], commission_percentage: null, commission_exempt_amount: null, commission_max_amount: null, max_number_sales: null, limite_jugada: null, alcance_limite_jugada: null }), [field]: val },
    }));
  };

  const agregarNumero = (tipoVar: string) => {
    const input = (numeroInput[tipoVar] || '').trim();
    if (!input) return;
    // Soporta entrada separada por comas, espacios
    const nuevos = input.split(/[,\s]+/).map(n => n.padStart(2, '0')).filter(n => /^\d{2}$/.test(n));
    if (nuevos.length === 0) return;
    setRestringidos(prev => {
      const existentes = prev[tipoVar] || [];
      const set = new Set(existentes);
      nuevos.forEach(n => set.add(n));
      return { ...prev, [tipoVar]: Array.from(set).sort() };
    });
    setNumeroInput(prev => ({ ...prev, [tipoVar]: '' }));
  };

  const quitarNumero = (tipoVar: string, num: string) => {
    setRestringidos(prev => ({
      ...prev,
      [tipoVar]: (prev[tipoVar] || []).filter(n => n !== num),
    }));
  };

  const guardar = async () => {
    if (!nombre.trim()) { setError('El nombre es requerido'); return; }

    const premiosList: { tipo_id: string; concepto: string; multiplicador: string; es_extranjera: boolean }[] = [];
    const configsList: any[] = [];

    // Para cada tipo activo, generar items de la variante nacional siempre
    // y de la variante extranjera si el banquero la activó.
    for (const tipo of tiposActivos) {
      const conceptos = CONCEPTOS_POR_TIPO[tipo] || [];
      const variantes: boolean[] = extranjeraActiva[tipo] ? [false, true] : [false];
      for (const extr of variantes) {
        const k = keyVar(tipo, extr);
        for (const concepto of conceptos) {
          const mult = premios[k]?.[concepto];
          if (mult && Number(mult) > 0) {
            premiosList.push({ tipo_id: tipo, concepto, multiplicador: mult, es_extranjera: extr });
          }
        }
        const cfg = configs[k];
        if (cfg) {
          configsList.push({
            tipo_id: tipo,
            commission_percentage: numOrNull(cfg.commission_percentage),
            commission_exempt_amount: numOrNull(cfg.commission_exempt_amount),
            commission_max_amount: numOrNull(cfg.commission_max_amount),
            max_number_sales: numOrNull(cfg.max_number_sales),
            limite_jugada: numOrNull(cfg.limite_jugada),
            alcance_limite_jugada: hasValue(cfg.alcance_limite_jugada) ? cfg.alcance_limite_jugada : null,
            es_extranjera: extr,
          });
        }
      }
    }

    // Armar lista de restringidos (por variante)
    const restringidosList: { tipo_id: string; numeros: string[]; auto_dia: boolean; max_number_sales?: number | null; riesgo_pct?: number | null; minimo_permitido?: number | null; es_extranjera: boolean }[] = [];
    for (const tipo of tiposActivos) {
      if (!TIPOS_CON_RESTRINGIDOS.has(tipo)) continue;
      const variantes: boolean[] = extranjeraActiva[tipo] ? [false, true] : [false];
      for (const extr of variantes) {
        const k = keyVar(tipo, extr);
        const nums = restringidos[k] || [];
        const haAutoDia = autoDia[k] || false;
        const limites = restLimites[k] || {};
        const tieneNumeros = nums.length > 0;
        const tieneLimites = hasValue(limites.max_number_sales) || hasValue(limites.riesgo_pct) || hasValue(limites.minimo_permitido);
        if (tieneNumeros || haAutoDia || tieneLimites) {
          restringidosList.push({
            tipo_id: tipo,
            numeros: nums,
            auto_dia: haAutoDia,
            max_number_sales: numOrNull(limites.max_number_sales),
            riesgo_pct: numOrNull(limites.riesgo_pct),
            minimo_permitido: numOrNull(limites.minimo_permitido),
            es_extranjera: extr,
          });
        }
      }
    }

    setGuardando(true);
    setError('');
    try {
      if (editando) {
        await fetchAPI<any>(`/paquetes-reglas/${editando.id}`, {
          method: 'PUT',
          body: JSON.stringify({ nombre: nombre.trim(), descripcion: descripcion.trim() || null, premios: premiosList, configs: configsList, restringidos: restringidosList }),
        });
      } else {
        await fetchAPI<any>('/paquetes-reglas', {
          method: 'POST',
          body: JSON.stringify({ nombre: nombre.trim(), descripcion: descripcion.trim() || null, premios: premiosList, configs: configsList, restringidos: restringidosList }),
        });
      }
      onSaved();
    } catch (e: any) {
      setError(e.message || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  // Render de los inputs de UNA variante (nacional o extranjera) para un tipo.
  // Usa keyVar(tipo, extr) para indexar los maps de state.
  const renderVariante = (tipo: string, extr: boolean, conceptos: string[]) => {
    const k = keyVar(tipo, extr);
    return (
      <>
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase mb-2">Premios (multiplicadores)</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {conceptos.map(concepto => (
              <div key={concepto}>
                <label className="block text-xs text-gray-600 mb-1">{CONCEPTO_LABELS[concepto] || concepto}</label>
                <div className="flex items-center gap-1">
                  <input
                    type="number" min="0" step="1"
                    value={premios[k]?.[concepto] || ''}
                    onChange={e => setMultiplicador(k, concepto, e.target.value)}
                    placeholder="0"
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  <span className="text-xs text-gray-400">×</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <p className="text-xs font-medium text-gray-500 uppercase mb-2">Config Comercial</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">% Comisión</label>
              <input
                type="number" min="0" step="0.5"
                value={hasValue(configs[k]?.commission_percentage) ? String(configs[k]!.commission_percentage) : ''}
                onChange={e => setConfigField(k, 'commission_percentage', hasValue(e.target.value) ? e.target.value : null)}
                placeholder="0"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Máx por número</label>
              <input
                type="number" min="0" step="1"
                value={hasValue(configs[k]?.max_number_sales) ? String(configs[k]!.max_number_sales) : ''}
                onChange={e => setConfigField(k, 'max_number_sales', numOrNull(e.target.value))}
                placeholder="Sin límite"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Límite jugada</label>
              <input
                type="number" min="0" step="1"
                value={hasValue(configs[k]?.limite_jugada) ? String(configs[k]!.limite_jugada) : ''}
                onChange={e => setConfigField(k, 'limite_jugada', hasValue(e.target.value) ? e.target.value : null)}
                placeholder="Sin límite"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {TIPOS_CON_RESTRINGIDOS.has(tipo) && (
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase mb-2">Números Restringidos</p>

            <label className="flex items-center gap-2 mb-3 cursor-pointer">
              <input
                type="checkbox"
                checked={autoDia[k] || false}
                onChange={e => setAutoDia(prev => ({ ...prev, [k]: e.target.checked }))}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Restringir automáticamente el número del día</span>
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Máx a recibir</label>
                <input
                  type="number" min="0" step="1"
                  value={hasValue(restLimites[k]?.max_number_sales) ? String(restLimites[k]!.max_number_sales) : ''}
                  onChange={e => setRestLimites(prev => ({
                    ...prev,
                    [k]: { ...(prev[k] || {}), max_number_sales: numOrNull(e.target.value) }
                  }))}
                  placeholder="Sin límite"
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">% Riesgo</label>
                <input
                  type="number" min="0" step="0.5"
                  value={hasValue(restLimites[k]?.riesgo_pct) ? String(restLimites[k]!.riesgo_pct) : ''}
                  onChange={e => setRestLimites(prev => ({
                    ...prev,
                    [k]: { ...(prev[k] || {}), riesgo_pct: hasValue(e.target.value) ? e.target.value : null }
                  }))}
                  placeholder="0"
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Mín permitido</label>
                <input
                  type="number" min="0" step="1"
                  value={hasValue(restLimites[k]?.minimo_permitido) ? String(restLimites[k]!.minimo_permitido) : ''}
                  onChange={e => setRestLimites(prev => ({
                    ...prev,
                    [k]: { ...(prev[k] || {}), minimo_permitido: hasValue(e.target.value) ? e.target.value : null }
                  }))}
                  placeholder="0"
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={numeroInput[k] || ''}
                onChange={e => setNumeroInput(prev => ({ ...prev, [k]: e.target.value }))}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); agregarNumero(k); } }}
                placeholder="Ej: 00, 13, 42"
                className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={() => agregarNumero(k)}
                className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded border border-blue-200"
              >
                Agregar
              </button>
            </div>
            {(restringidos[k]?.length || 0) > 0 && (
              <div className="flex flex-wrap gap-1">
                {restringidos[k].map(num => (
                  <span key={num} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono bg-red-50 text-red-700 border border-red-200 rounded-full">
                    {num}
                    <button onClick={() => quitarNumero(k, num)} className="text-red-400 hover:text-red-600">
                      <X size={10} />
                    </button>
                  </span>
                ))}
              </div>
            )}
            {(restringidos[k]?.length || 0) === 0 && !autoDia[k] && (
              <p className="text-xs text-gray-400">Sin números restringidos</p>
            )}
          </div>
        )}
      </>
    );
  };

  return (
    <Modal isOpen onClose={onClose} title={editando ? `Editar: ${editando.nombre}` : 'Nuevo Paquete'} size="lg">
      <div className="space-y-4 max-h-[70vh] overflow-y-auto">
        {error && <div className="p-2 bg-red-50 text-red-700 text-sm rounded">{error}</div>}

        {editando && editando.locales_asignados > 0 && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
            <AlertTriangle size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-amber-700">
              Este paquete está asignado a <strong>{editando.locales_asignados} local(es)</strong>. Los cambios se aplicarán inmediatamente.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
            <input
              type="text" value={nombre} onChange={e => setNombre(e.target.value)}
              placeholder="Ej: Normal 85, Riesgo bajo..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
            <input
              type="text" value={descripcion} onChange={e => setDescripcion(e.target.value)}
              placeholder="Opcional"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="border-t pt-4">
          <p className="text-sm font-medium text-gray-700 mb-3">Reglas por tipo de juego</p>
          <div className="space-y-2">
            {TIPOS_ORDEN.map(tipo => {
              const activo = tiposActivos.has(tipo);
              const expanded = expandedTipos.has(tipo);
              const conceptos = CONCEPTOS_POR_TIPO[tipo];

              return (
                <div key={tipo} className={`border rounded-lg ${activo ? 'border-blue-300 bg-blue-50/30' : 'border-gray-200'}`}>
                  <div className="flex items-center justify-between px-4 py-2.5">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => toggleTipo(tipo)}
                        className={`w-5 h-5 rounded border flex items-center justify-center ${activo ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-300'}`}
                      >
                        {activo && <Check size={14} />}
                      </button>
                      <span className={`font-medium ${activo ? 'text-gray-900' : 'text-gray-400'}`}>{tipo}</span>
                      <span className="text-xs text-gray-400">({conceptos.length} concepto{conceptos.length > 1 ? 's' : ''})</span>
                    </div>
                    {activo && (
                      <button onClick={() => toggleExpand(tipo)} className="text-gray-400 hover:text-gray-600">
                        {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                      </button>
                    )}
                  </div>

                  {activo && expanded && (
                    <div className="px-4 pb-4 space-y-3 border-t border-blue-200">
                      {/* Toggle: definir variante extranjera para este tipo */}
                      <div className="pt-3 flex items-center gap-2">
                        <input
                          id={`extr-toggle-${tipo}`}
                          type="checkbox"
                          checked={!!extranjeraActiva[tipo]}
                          onChange={e => setExtranjeraActiva(prev => ({ ...prev, [tipo]: e.target.checked }))}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <label htmlFor={`extr-toggle-${tipo}`} className="text-sm text-gray-700 select-none">
                          Definir variante <strong>extranjera</strong> para este tipo
                          <span className="block text-xs text-gray-500">
                            Si no la defines, los sorteos extranjeros usarán la configuración nacional.
                          </span>
                        </label>
                      </div>

                      {/* Variante NACIONAL */}
                      <div className="rounded-lg border border-gray-200 p-3 space-y-3">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">Nacional</span>
                          <span className="text-[10px] text-gray-400">Aplica a sorteos donde es_extranjera=false</span>
                        </div>
                        {renderVariante(tipo, false, conceptos)}
                      </div>

                      {extranjeraActiva[tipo] && (
                        <div className="rounded-lg border border-amber-300 bg-amber-50/40 p-3 space-y-3">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold uppercase tracking-wide text-amber-700">Extranjera</span>
                            <span className="text-[10px] text-amber-600">Aplica a sorteos donde es_extranjera=true</span>
                          </div>
                          {renderVariante(tipo, true, conceptos)}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex gap-2 justify-end pt-4 border-t sticky bottom-0 bg-white">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button variant="primary" onClick={guardar} disabled={guardando} leftIcon={<Save size={16} />}>
            {guardando ? 'Guardando...' : editando ? 'Guardar Cambios' : 'Crear Paquete'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};


// ── Tab 2: Asignar Locales ──────────────────────────────

const AsignarTab: React.FC = () => {
  const [paquetes, setPaquetes] = useState<PaqueteResumen[]>([]);
  const [locales, setLocales] = useState<LocalConPaquete[]>([]);
  const [paqueteSeleccionado, setPaqueteSeleccionado] = useState('');
  const [seleccionados, setSeleccionados] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [asignando, setAsignando] = useState(false);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');

  const cargar = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [paq, loc] = await Promise.all([
        fetchAPI<PaqueteResumen[]>('/paquetes-reglas'),
        fetchAPI<LocalConPaquete[]>('/paquetes-reglas/locales'),
      ]);
      setPaquetes(paq);
      setLocales(loc);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const toggleLocal = (id: string) => {
    setSeleccionados(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const seleccionarTodos = () => {
    if (seleccionados.size === locales.length) {
      setSeleccionados(new Set());
    } else {
      setSeleccionados(new Set(locales.map(l => l.id)));
    }
  };

  const asignar = async () => {
    if (!paqueteSeleccionado || seleccionados.size === 0) return;
    setAsignando(true);
    setError('');
    setMensaje('');
    try {
      const result = await fetchAPI<any>(`/paquetes-reglas/${paqueteSeleccionado}/asignar`, {
        method: 'POST',
        body: JSON.stringify({ local_ids: Array.from(seleccionados) }),
      });
      setMensaje(
        `Paquete "${result.paquete}" aplicado a ${result.exitosos} local(es).` +
        (result.reemplazados > 0 ? ` ${result.reemplazados} reemplazado(s).` : '')
      );
      setSeleccionados(new Set());
      cargar();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setAsignando(false);
    }
  };

  const desasignar = async (localId: string, paqueteId: string) => {
    try {
      await fetchAPI<any>(`/paquetes-reglas/${paqueteId}/desasignar`, {
        method: 'POST',
        body: JSON.stringify({ local_ids: [localId] }),
      });
      setMensaje('Paquete removido del local');
      cargar();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const paqNombre = paquetes.find(p => p.id === paqueteSeleccionado)?.nombre || '';
  const localesConReemplazo = Array.from(seleccionados).filter(id => {
    const local = locales.find(l => l.id === id);
    return local?.paquete_reglas_id && local.paquete_reglas_id !== paqueteSeleccionado;
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Asignar Paquetes a Locales</h1>
        <p className="text-gray-500">Seleccione un paquete y los locales donde aplicarlo</p>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>}
      {mensaje && <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700">{mensaje}</div>}

      <Card>
        <div className="flex flex-col md:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Paquete a aplicar</label>
            <select
              value={paqueteSeleccionado}
              onChange={e => { setPaqueteSeleccionado(e.target.value); setSeleccionados(new Set()); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Seleccione un paquete</option>
              {paquetes.filter(p => p.activo).map(p => (
                <option key={p.id} value={p.id}>{p.nombre} ({p.locales_asignados} locales)</option>
              ))}
            </select>
          </div>
          <Button
            variant="primary"
            onClick={asignar}
            disabled={!paqueteSeleccionado || seleccionados.size === 0 || asignando}
          >
            {asignando ? 'Aplicando...' : `Aplicar a ${seleccionados.size} local(es)`}
          </Button>
        </div>
      </Card>

      {localesConReemplazo.length > 0 && paqueteSeleccionado && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
          <AlertTriangle size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-amber-700">
            <strong>{localesConReemplazo.length} local(es)</strong> ya tienen otro paquete asignado. Al aplicar "{paqNombre}", su paquete actual será reemplazado.
          </p>
        </div>
      )}

      <Card padding="none">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Cargando...</div>
        ) : locales.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No hay locales configurados</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="px-4 py-3 w-10">
                    {paqueteSeleccionado && (
                      <button
                        onClick={seleccionarTodos}
                        className={`w-5 h-5 rounded border flex items-center justify-center ${seleccionados.size === locales.length ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-300'}`}
                      >
                        {seleccionados.size === locales.length && <Check size={14} />}
                      </button>
                    )}
                  </th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Local</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Paquete Actual</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-gray-700 w-24">Acción</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {locales.map(l => {
                  const selected = seleccionados.has(l.id);
                  const tieneOtroPaquete = l.paquete_reglas_id && l.paquete_reglas_id !== paqueteSeleccionado;
                  return (
                    <tr key={l.id} className={`hover:bg-gray-50 ${selected ? 'bg-blue-50/50' : ''}`}>
                      <td className="px-4 py-3">
                        {paqueteSeleccionado && (
                          <button
                            onClick={() => toggleLocal(l.id)}
                            className={`w-5 h-5 rounded border flex items-center justify-center ${selected ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-300'}`}
                          >
                            {selected && <Check size={14} />}
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Store size={16} className="text-gray-400" />
                          <span className="font-medium text-gray-900">{l.nombre}</span>
                          {l.es_propio && <Badge variant="neutral">Propio</Badge>}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {l.paquete_nombre ? (
                          <div className="flex items-center gap-2">
                            <Badge variant="primary">{l.paquete_nombre}</Badge>
                            {selected && tieneOtroPaquete && (
                              <span className="text-xs text-amber-600 font-medium">Se reemplazará</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">Sin paquete</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {l.paquete_reglas_id && (
                          <button
                            onClick={() => desasignar(l.id, l.paquete_reglas_id!)}
                            className="text-xs text-red-600 hover:text-red-800 font-medium"
                          >
                            Quitar
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};


// ── Tab 3: Bloqueados Globales ──────────────────────────

interface BloqueadoGlobal {
  tipo_id: string;
  numeros: string[];
}

const BloqueadosTab: React.FC = () => {
  const [bloqueados, setBloqueados] = useState<BloqueadoGlobal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [tipoSeleccionado, setTipoSeleccionado] = useState('NORMAL');
  const [numeroInput, setNumeroInput] = useState('');
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchAPI<BloqueadoGlobal[]>('/paquetes-reglas/bloqueados-globales');
      setBloqueados(data);
    } catch (e: any) {
      setError(e.message || 'Error al cargar bloqueados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const agregar = async () => {
    const input = numeroInput.trim();
    if (!input) return;
    const nuevos = input.split(/[,\s]+/).map(n => n.padStart(2, '0')).filter(n => /^\d{2}$/.test(n));
    if (nuevos.length === 0) { setError('Ingrese números de 2 dígitos (00-99)'); return; }

    setGuardando(true);
    setError('');
    setMensaje('');
    try {
      await fetchAPI<any>('/paquetes-reglas/bloqueados-globales', {
        method: 'POST',
        body: JSON.stringify({ tipo_id: tipoSeleccionado, numeros: nuevos }),
      });
      setNumeroInput('');
      setMensaje(`${nuevos.length} número(s) bloqueado(s) en ${tipoSeleccionado}`);
      cargar();
    } catch (e: any) {
      setError(e.message || 'Error al bloquear');
    } finally {
      setGuardando(false);
    }
  };

  const quitar = async (tipo_id: string, numero: string) => {
    setError('');
    setMensaje('');
    try {
      await fetchAPI<any>(`/paquetes-reglas/bloqueados-globales/${tipo_id}/${numero}`, {
        method: 'DELETE',
      });
      setMensaje(`Número ${numero} desbloqueado de ${tipo_id}`);
      cargar();
    } catch (e: any) {
      setError(e.message || 'Error al desbloquear');
    }
  };

  const tiposDisponibles = ['NORMAL', 'REVENTADO'];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Números Bloqueados</h1>
        <p className="text-gray-500">Números con venta prohibida en todos los locales de la banca</p>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>}
      {mensaje && <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700">{mensaje}</div>}

      <Card>
        <p className="text-sm font-medium text-gray-700 mb-3">Bloquear números</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Tipo</label>
            <select
              value={tipoSeleccionado}
              onChange={e => setTipoSeleccionado(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              {tiposDisponibles.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-xs text-gray-500 mb-1">Números (separados por coma o espacio)</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={numeroInput}
                onChange={e => setNumeroInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); agregar(); } }}
                placeholder="Ej: 00, 13, 42"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <Button
                variant="primary"
                onClick={agregar}
                disabled={guardando || !numeroInput.trim()}
                leftIcon={<Ban size={16} />}
              >
                {guardando ? 'Bloqueando...' : 'Bloquear'}
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {loading ? (
        <div className="p-8 text-center text-gray-500">Cargando...</div>
      ) : bloqueados.length === 0 ? (
        <Card>
          <div className="p-4 text-center text-gray-500">No hay números bloqueados. Agregue números arriba para prohibir su venta.</div>
        </Card>
      ) : (
        <div className="space-y-4">
          {bloqueados.map(grupo => (
            <Card key={grupo.tipo_id}>
              <div className="flex items-center gap-2 mb-3">
                <Ban size={16} className="text-purple-600" />
                <h3 className="font-medium text-gray-900">{grupo.tipo_id}</h3>
                <Badge variant="neutral">{grupo.numeros.length} número{grupo.numeros.length !== 1 ? 's' : ''}</Badge>
              </div>
              <div className="flex flex-wrap gap-2">
                {grupo.numeros.map(num => (
                  <span key={num} className="inline-flex items-center gap-1.5 px-3 py-1 text-sm font-mono bg-purple-50 text-purple-700 border border-purple-200 rounded-full">
                    {num}
                    <button onClick={() => quitar(grupo.tipo_id, num)} className="text-purple-400 hover:text-purple-600" title="Desbloquear">
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};


// ── Componente principal ────────────────────────────────

export const PaquetesReglasPage: React.FC = () => {
  return (
    <Tabs
      items={[
        {
          key: 'paquetes',
          label: 'Paquetes',
          icon: <Package size={16} />,
          content: <PaquetesListTab />,
        },
        {
          key: 'asignar',
          label: 'Asignar Locales',
          icon: <Store size={16} />,
          content: <AsignarTab />,
        },
        {
          key: 'bloqueados',
          label: 'Bloqueados',
          icon: <Ban size={16} />,
          content: <BloqueadosTab />,
        },
      ]}
    />
  );
};

export default PaquetesReglasPage;
